/* ════════════════════════════════════════════════════════════════════════════
   Every kind of document this app syncs, declared once.

   A family only actually travels if five separate places agree about it, and
   none of them can see the other four: something has to OFFER the key, the
   server's KEY_PATTERN has to store it, `mdSanitiseDoc` has to let it back in,
   `mdApplyPulled` has to know who to hand it to, and the pull loop's `merges()`
   has to know whether a conflict over it is fatal.

   Four out of five is not a smaller feature. It is an INERT one, and it fails
   silently on every side. `navigation`, `view:`, `form:` and `release:` each
   had an offer, a pattern, an applier, and a module that owned the shape — and
   no branch in the sanitiser. Every device pushed them happily; no device could
   ever receive one. Nothing threw, nothing logged, and four families were
   one-way for as long as they had existed. Nobody had decided that. It was
   five decisions in five files with nowhere to notice they disagreed.

   So this is the nowhere. It is a DECLARATION, not a dispatcher: the five
   layers keep their own code, and tests/families-declared-once.test.js reads
   this file and asks each of them whether it agrees. That direction is
   deliberate. `mdSanitiseDoc` is a long function whose per-family comments
   carry real history — why a board has two legitimate shapes, why a null
   tombstone is unusable, why the record index is refused — and rewriting it to
   be driven from a table would throw all of that away to remove a duplication
   a test already catches.

   AND IT IS THE MAP FOR WHAT COMES NEXT. A Nostr relay stores parameterised
   replaceable events keyed by a `d` tag, which is what a document key already
   is; a Blossom server stores blobs by hash, which is what md-blobs.ts already
   does. The per-family answers a switch needs — does it merge, who owns the
   shape, is it one-way and why — are exactly the columns below.
   ════════════════════════════════════════════════════════════════════════════ */
(function (g: any) {
  type MdFamily = {
    /** How this family is spoken about. Not a key — `board:` and `records:<day>`
        are names, and one of them matches many keys. */
    name: string;
    /** One line of English: what a document of this kind IS. */
    what: string;
    /** The keys it matches. Anchored, and checked against server/sync.js. */
    pattern: RegExp;
    /** One key the family really uses, for every layer to be asked about. */
    sample: string;
    /** A value the sanitiser must accept. Where the sanitiser delegates, the
        delegate owns the real shape and this is only enough to reach it. */
    value: unknown;
    /** The family’s OTHER legitimate wire shapes, where it has any. A desk that
        fits and one that spilled to its own bytes are both desks; a picture as
        a payload from an older build and as a name from this one are both
        pictures. A door that carries half a family is inert by a narrower
        road, so both halves are asked about. */
    alsoValid?: unknown[];
    /** The module that owns the shape. */
    owner: string;
    /** Does a document of this kind come BACK? `false` is a decision, and `why`
        is then required — see the test that reads it. */
    arrives: boolean;
    why?: string;
    /** Can it be told one of its documents is gone? `false` for a tombstone,
        which has no such thing as a deletion. */
    deletable?: boolean;
    /** The global `mdApplyPulled` hands it to, or `(inline)` where the router
        does the work itself. */
    applier: string | null;
    /** The family door the pull loop's `merges()` asks about it, or null for
        last-write-wins. */
    merges: string | null;
    /** Whether a live room carries it, or it waits for the ordinary sync. The
        list this must agree with is CARRIES in app/md-live-frame.ts. */
    live: 'carry' | 'nudge';
  };

  const DAY = '2026-08-31';
  const HASH = 'a'.repeat(64);

  const MD_FAMILIES: MdFamily[] = [
    /* ── the day, which is where this app started ────────────────────────── */
    { name: 'events:<day>', what: 'one day’s timed items',
      pattern: /^events:\d{4}-\d{2}-\d{2}$/, sample: `events:${DAY}`,
      value: [{ uid: 'a', kind: 'event', min: 540 }], owner: 'app/md-store.js',
      arrives: true, applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },
    { name: 'records:<day>', what: 'one day’s records',
      pattern: /^records:\d{4}-\d{2}-\d{2}$/, sample: `records:${DAY}`,
      value: [{ uid: 'a' }], owner: 'app/md-store.js',
      arrives: true, applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },
    { name: 'completed:<day>', what: 'what was ticked off that day — a grow-only set',
      pattern: /^completed:\d{4}-\d{2}-\d{2}$/, sample: `completed:${DAY}`,
      value: { a: true }, owner: 'app/md-store.js',
      arrives: true, applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },
    { name: 'tombstones:<day>', what: 'what was deleted that day, and when',
      pattern: /^tombstones:\d{4}-\d{2}-\d{2}$/, sample: `tombstones:${DAY}`,
      value: { a: 1756000000000 }, owner: 'app/md-store.js',
      /* THERE IS NO DELETION OF A TOMBSTONE. One accepted null here undeletes a
         day of records on every device that pulls it. See md-sync.js. */
      arrives: true, deletable: false,
      applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },
    { name: 'crdt:events:<day>', what: 'the Yjs state riding beside a day’s events',
      pattern: /^crdt:events:\d{4}-\d{2}-\d{2}$/, sample: `crdt:events:${DAY}`,
      value: 'AQIDBAU=', owner: 'app/md-store.js',
      arrives: true, applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },
    { name: 'crdt:records:<day>', what: 'the Yjs state riding beside a day’s records',
      pattern: /^crdt:records:\d{4}-\d{2}-\d{2}$/, sample: `crdt:records:${DAY}`,
      value: 'AQIDBAU=', owner: 'app/md-store.js',
      arrives: true, applier: 'mdStoreApplySyncDoc', merges: 'mdStoreMergesDoc', live: 'carry' },

    /* ── the account’s own settings ──────────────────────────────────────── */
    { name: 'settings', what: 'where the person is, how prayer times are calculated',
      pattern: /^settings$/, sample: 'settings',
      value: { location: null, calc: null, hijriAdjust: null }, owner: 'app/md-sync.js',
      /* The one family the router applies itself: three individual values, no
         module of their own, and splitting them into one would be a migration
         that buys nothing. */
      arrives: true, applier: '(inline)', merges: null, live: 'nudge' },
    { name: 'navigation', what: 'what was opened lately and what is kept close',
      pattern: /^navigation$/, sample: 'navigation',
      value: { recents: {}, favorites: {} }, owner: 'app/md-navigation.ts',
      arrives: true, applier: 'mdNavigationApplyDoc', merges: 'mdNavigationMergesDoc', live: 'nudge' },
    { name: 'devices', what: 'the devices on this account, one row each',
      pattern: /^devices$/, sample: 'devices', value: { d1: { name: 'iPad' } },
      owner: 'app/md-devices.ts',
      arrives: true, applier: 'mdDevicesApplyDoc', merges: 'mdDevicesMergesDoc', live: 'nudge' },
    { name: 'profile', what: 'who the account belongs to, field by field',
      pattern: /^profile$/, sample: 'profile', value: { name: { v: 'A', at: 1 } },
      owner: 'app/md-person.ts',
      arrives: true, applier: 'mdPersonApplyDoc', merges: 'mdPersonMergesDoc', live: 'nudge' },

    /* ── desks ───────────────────────────────────────────────────────────── */
    { name: 'boards', what: 'the shelf: which desks exist, and what they are called',
      pattern: /^boards$/, sample: 'boards', value: [{ id: 'abcdef', name: 'Ramadan plan' }],
      owner: 'app/md-board.ts',
      arrives: true, applier: 'mdBoardApplyDoc', merges: 'mdBoardMergesDoc', live: 'nudge' },
    { name: 'desk-tombstones', what: 'which desks are gone, and when',
      pattern: /^desk-tombstones$/, sample: 'desk-tombstones', value: { abcdef: 1756000000000 },
      owner: 'app/md-board.ts',
      /* A tombstone for the same reason a day’s is: accepting a null here lets
         a stale whole-list push bring every deleted desk back. */
      arrives: true, deletable: false,
      applier: 'mdBoardApplyDoc', merges: 'mdBoardMergesDoc', live: 'nudge' },
    { name: 'board:', what: 'one desk’s drawing — or the name of the bytes it spilled to',
      pattern: /^board:[a-z0-9]{6,40}$/, sample: 'board:abcdef',
      value: { elements: [{ id: 'e1' }], at: 1 },
      alsoValid: [{ spill: { hash: HASH, size: 4096, blob: 1 }, elements: 12, at: 1 }],
      owner: 'app/md-board.ts',
      arrives: true, applier: 'mdBoardApplyDoc', merges: 'mdBoardMergesDoc', live: 'carry' },
    { name: 'boardfile:', what: 'a picture on a desk — a payload from an older build, a name from this one',
      pattern: /^boardfile:[a-z0-9]{6,64}$/, sample: 'boardfile:abcdef',
      value: { id: 'abcdef', mimeType: 'image/png', hash: HASH, size: 4096, blob: 1, created: 1 },
      alsoValid: [{ id: 'abcdef', mimeType: 'image/png', dataURL: 'data:image/png;base64,AAA=', created: 1 }],
      owner: 'app/md-board.ts',
      arrives: true, applier: 'mdBoardApplyDoc', merges: null, live: 'carry' },

    /* ── the spine: domains, pursuits, projects, and where records stand ─── */
    { name: 'domain:', what: 'one of the ten contexts, and the writing in it',
      pattern: /^domain:e\d{1,3}$/, sample: 'domain:e12', value: { text: 'x', at: 1 },
      owner: 'app/md-domain.ts',
      arrives: true, applier: 'mdDomainApplyDoc', merges: null, live: 'nudge' },
    { name: 'pursuits', what: 'the enduring parents between a domain and a finite project',
      pattern: /^pursuits$/, sample: 'pursuits', value: [{ id: 'abcdef', domainId: 'e1' }],
      owner: 'app/md-pursuit.ts',
      arrives: true, applier: 'mdPursuitApplyDoc', merges: null, live: 'nudge' },
    { name: 'pursuit:', what: 'one pursuit’s writing',
      pattern: /^pursuit:[a-z0-9]{6,40}$/, sample: 'pursuit:abcdef', value: { text: 'x', at: 1 },
      owner: 'app/md-pursuit.ts',
      arrives: true, applier: 'mdPursuitApplyDoc', merges: null, live: 'nudge' },
    { name: 'projects', what: 'the finite ones, each under a domain',
      pattern: /^projects$/, sample: 'projects', value: [{ id: 'abcdef', domainId: 'e1' }],
      owner: 'app/md-project.ts',
      arrives: true, applier: 'mdProjectApplyDoc', merges: null, live: 'nudge' },
    { name: 'project:', what: 'one project’s writing',
      pattern: /^project:[a-z0-9]{6,40}$/, sample: 'project:abcdef', value: { text: 'x', at: 1 },
      owner: 'app/md-project.ts',
      arrives: true, applier: 'mdProjectApplyDoc', merges: null, live: 'nudge' },
    { name: 'project-records', what: 'which record belongs to which project',
      pattern: /^project-records$/, sample: 'project-records', value: [{ uid: 'a', pid: 'abcdef' }],
      owner: 'app/md-project-record.ts',
      arrives: true, applier: 'mdProjectRecordApplyDoc', merges: null, live: 'nudge' },
    { name: 'record-status', what: 'where every record stands, by word',
      pattern: /^record-status$/, sample: 'record-status', value: { a1b2c3: 'doing' },
      owner: 'app/md-record-status.ts',
      arrives: true, applier: 'mdRecordStatusApplyDoc', merges: null, live: 'carry' },
    { name: 'workflows', what: 'the status vocabularies somebody has named',
      pattern: /^workflows$/, sample: 'workflows', value: [{ id: 'abcdef', statuses: [] }],
      owner: 'app/md-workflow.ts',
      arrives: true, applier: 'mdWorkflowApplyDoc', merges: null, live: 'nudge' },
    { name: 'workflow-use', what: 'which vocabulary each domain, pursuit and project uses',
      pattern: /^workflow-use$/, sample: 'workflow-use', value: { e1: 'abcdef' },
      owner: 'app/md-workflow.ts',
      arrives: true, applier: 'mdWorkflowApplyDoc', merges: null, live: 'nudge' },

    /* ── how things are looked at ────────────────────────────────────────── */
    { name: 'project-views', what: 'the Workshop’s saved views, all of them, in one document',
      pattern: /^project-views$/, sample: 'project-views', value: { views: [] },
      owner: 'app/md-workshop.ts',
      arrives: true, applier: 'mdWorkshopApplyDoc', merges: null, live: 'nudge' },
    { name: 'record-views:', what: 'how ONE project’s records are looked at',
      pattern: /^record-views:[a-z0-9]{6,40}$/, sample: 'record-views:abcdef', value: { views: [] },
      owner: 'app/md-worklist.ts',
      arrives: true, applier: 'mdWorklistApplyDoc', merges: null, live: 'nudge' },

    /* ── inline databases ────────────────────────────────────────────────── */
    { name: 'database:', what: 'one inline database’s metadata — rows and views are their own documents',
      pattern: /^database:[a-z0-9]{6,40}$/, sample: 'database:abcdef',
      value: { id: 'abcdef', properties: [], rows: [], views: [] }, owner: 'app/md-database.ts',
      arrives: true, applier: 'mdDatabaseApplyDoc', merges: null, live: 'nudge' },
    { name: 'database-row:', what: 'one row of one database',
      pattern: /^database-row:[a-z0-9]{6,40}:[a-z0-9]{6,40}$/, sample: 'database-row:abcdef:ghijkl',
      value: { id: 'ghijkl', values: { title: 'x' } }, owner: 'app/md-database.ts',
      arrives: true, applier: 'mdDatabaseApplyDoc', merges: null, live: 'carry' },
    { name: 'crdt:database-row:', what: 'the Yjs state riding beside a row, where the grain is a cell',
      pattern: /^crdt:database-row:[a-z0-9]{6,40}:[a-z0-9]{6,40}$/,
      sample: 'crdt:database-row:abcdef:ghijkl', value: 'AQIDBAU=', owner: 'app/md-database.ts',
      arrives: true, applier: 'mdDatabaseApplyDoc', merges: 'mdDatabaseMergesDoc', live: 'carry' },
    { name: 'view:', what: 'one saved way of looking at a database',
      pattern: /^view:[a-z0-9]{6,40}$/, sample: 'view:abcdef',
      value: { id: 'abcdef', databaseId: 'ghijkl' }, owner: 'app/md-database.ts',
      arrives: true, applier: 'mdDatabaseApplyDoc', merges: null, live: 'nudge' },
    { name: 'form:', what: 'the input contract over a database',
      pattern: /^form:[a-z0-9]{6,40}$/, sample: 'form:abcdef',
      value: { id: 'abcdef', databaseId: 'ghijkl' }, owner: 'app/md-database.ts',
      arrives: true, applier: 'mdDatabaseApplyDoc', merges: null, live: 'nudge' },

    /* ── membership, and the organizer that guards it ────────────────────── */
    { name: 'memberships:', what: 'the ordering projection over one database’s members',
      pattern: /^memberships:[a-z0-9]{6,40}$/, sample: 'memberships:abcdef', value: { version: 1 },
      owner: 'app/md-membership.ts',
      arrives: true, applier: 'mdMembershipApplyDoc', merges: null, live: 'nudge' },
    { name: 'edge:', what: 'the authority document for one record’s place in one database',
      pattern: /^edge:[a-z0-9]{6,40}$/, sample: 'edge:abcdef', value: { version: 1 },
      owner: 'app/md-membership.ts',
      arrives: true, applier: 'mdMembershipApplyDoc', merges: null, live: 'nudge' },
    { name: 'proposal:', what: 'one immutable candidate for one membership cell',
      pattern: /^proposal:[a-z0-9]{6,40}$/, sample: 'proposal:abcdef', value: { version: 1 },
      owner: 'app/md-organizer.ts',
      arrives: true, applier: 'mdOrganizerApplyDoc', merges: null, live: 'nudge' },
    { name: 'decision:', what: 'one append-only review event over a proposal',
      pattern: /^decision:[a-z0-9]{6,40}$/, sample: 'decision:abcdef', value: { version: 1 },
      owner: 'app/md-organizer.ts',
      arrives: true, applier: 'mdOrganizerApplyDoc', merges: null, live: 'nudge' },
    { name: 'orgschema:', what: 'a versioned authority a guarded apply pins in its read-set',
      pattern: /^orgschema:[a-z0-9]{6,40}$/, sample: 'orgschema:abcdef', value: { version: 1 },
      owner: 'app/md-organizer.ts',
      arrives: true, applier: 'mdOrganizerApplyDoc', merges: null, live: 'nudge' },
    { name: 'orgpolicy:', what: 'the policy half of the same pair',
      pattern: /^orgpolicy:[a-z0-9]{6,40}$/, sample: 'orgpolicy:abcdef', value: { version: 1 },
      owner: 'app/md-organizer.ts',
      arrives: true, applier: 'mdOrganizerApplyDoc', merges: null, live: 'nudge' },

    /* ── what was published, what was thrown away, what happened ─────────── */
    { name: 'release:', what: 'one published manifest, immutable once it exists',
      pattern: /^release:[a-z0-9]{6,40}$/, sample: 'release:abcdef',
      value: { version: 1, id: 'abcdef', file: 'ghijkl', audience: 'private' },
      owner: 'app/md-manifest.ts',
      arrives: true, applier: 'mdReleaseApplyDoc', merges: null, live: 'nudge' },
    { name: 'trash:', what: 'one deletion, naming the whole subtree it took with it',
      pattern: /^trash:[a-z][a-z0-9]{0,15}:[a-z0-9]{1,64}$/, sample: 'trash:record:abc123',
      value: { at: 1 }, owner: 'app/md-trash.ts',
      arrives: true, applier: 'mdTrashApplyDoc', merges: null, live: 'nudge' },
    { name: 'history:', what: 'one DEVICE’S log for one record — the key names its writer',
      pattern: /^history:[a-z][a-z0-9]{0,15}:[a-z0-9]{1,64}:[a-z0-9]{1,64}$/,
      sample: 'history:record:abc123:dev1', value: { entries: [] }, owner: 'app/md-versions.ts',
      arrives: true, applier: 'mdHistoryLogApplyDoc', merges: 'mdHistoryLogMergesDoc', live: 'nudge' },

    /* ── the two that do not come back, each for its own reason ──────────── */
    { name: 'record-index', what: 'what this device concluded about where every record is',
      pattern: /^record-index$/, sample: 'record-index', value: {},
      owner: 'app/md-record-index.ts',
      arrives: false,
      why: 'A conclusion this device draws from documents it already holds. One '
        + 'drawn on another device could only be older, and an index that has not '
        + 'heard about a record hides it. Held in both directions: never offered, '
        + 'and refused by name on the way in rather than left to fall off the end '
        + 'of the sanitiser, because "we never wrote this down" is how a rule like '
        + 'this quietly stops being true.',
      applier: null, merges: null, live: 'nudge' },
    { name: 'record:', what: 'one canonical record, singular — the Phase 2 shape',
      pattern: /^record:[a-z0-9]{6,40}$/, sample: 'record:abcdef', value: {},
      owner: 'none yet — no client module writes this',
      arrives: false,
      why: 'A shape the server learned ahead of the client that will write it, '
        + 'per server/sync.js’s own rule that a key must deploy before the client '
        + 'writing it. Nothing offers one, nothing applies one, and giving it a '
        + 'sanitiser today would be inventing a contract for a document whose '
        + 'owner does not exist yet. It is reserved, not push-only.',
      applier: null, merges: null, live: 'nudge' },
  ];

  /** Which family a key belongs to, or null for a key nothing here claims. */
  function mdFamilyOf(key: unknown): MdFamily | null {
    if (typeof key !== 'string' || !key) return null;
    for (const fam of MD_FAMILIES) if (fam.pattern.test(key)) return fam;
    return null;
  }

  Object.assign(g, { MD_FAMILIES, mdFamilyOf });
})(typeof window !== 'undefined' ? window : globalThis);
